import React from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  ACTION_PRIORITY_OPTIONS,
  ACTION_STATUS_OPTIONS,
  ORGANIZATION,
} from 'components/IAP/ICSConstants';
import { useActionAssigneeDirectory } from 'components/IAP/hooks/useActionAssigneeDirectory';
import { useAorText } from 'utils/aorTerminology';
import type {
  TaskSectionArea,
  TaskSectionFilter,
  TaskSectionScope,
} from './taskSectionFilter';

type TaskSectionFilterFieldsProps = {
  value: TaskSectionFilter;
  onChange: (next: TaskSectionFilter) => void;
};

const SCOPE_OPTIONS: Array<{ value: TaskSectionScope; label: string }> = [
  { value: 'follow', label: 'Match the report' },
  { value: 'incident', label: 'Incident tasks' },
  { value: 'org', label: 'Org tasks' },
  { value: 'both', label: 'Both' },
];

function toggle(list: string[], value: string): string[] {
  return list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];
}

function CheckboxList({
  idPrefix,
  label,
  options,
  selected,
  onChange,
}: {
  idPrefix: string;
  label: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="tw:flex tw:min-w-0 tw:flex-row tw:flex-wrap tw:items-center tw:gap-x-4 tw:gap-y-2"
    >
      {options.map((option) => {
        const id = `${idPrefix}-${option.value}`;
        return (
          <div key={option.value} className="tw:flex tw:items-center tw:gap-2">
            <Checkbox
              id={id}
              checked={selected.includes(option.value)}
              onCheckedChange={() => onChange(toggle(selected, option.value))}
            />
            <Label htmlFor={id} className="tw:font-normal">
              {option.label}
            </Label>
          </div>
        );
      })}
    </div>
  );
}

/** Section settings: which tasks a report Tasks section shows. */
export default function TaskSectionFilterFields({
  value,
  onChange,
}: TaskSectionFilterFieldsProps) {
  const aorText = useAorText();
  const directory = useActionAssigneeDirectory(ORGANIZATION);
  const areaOptions: Array<{ value: TaskSectionArea; label: string }> = [
    { value: 'report', label: aorText('Report area or AOR') },
    { value: 'aor', label: aorText('Report AOR only') },
    { value: 'none', label: 'Anywhere' },
  ];
  const set = (patch: Partial<TaskSectionFilter>) =>
    onChange({ ...value, ...patch });

  return (
    <>
      <Field>
        <FieldLabel>Tasks shown</FieldLabel>
        <FieldDescription>
          The section lists real tasks from the Tasks tab. Changes to a task in
          either place show in both.
        </FieldDescription>
        <ToggleGroup
          variant="outline"
          size="sm"
          multiple={false}
          className="tw:w-full tw:max-w-full tw:flex-wrap"
          value={[value.scope]}
          onValueChange={(next) => {
            const scope = SCOPE_OPTIONS.find(
              (option) => option.value === next[0]
            );
            if (scope) set({ scope: scope.value });
          }}
          aria-label="Task table"
        >
          {SCOPE_OPTIONS.map((option) => (
            <ToggleGroupItem key={option.value} value={option.value}>
              {option.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </Field>
      <Field>
        <FieldLabel>Where</FieldLabel>
        <ToggleGroup
          variant="outline"
          size="sm"
          multiple={false}
          className="tw:w-full tw:max-w-full tw:flex-wrap"
          value={[value.area]}
          onValueChange={(next) => {
            const area = areaOptions.find((option) => option.value === next[0]);
            if (area) set({ area: area.value });
          }}
          aria-label="Task area"
        >
          {areaOptions.map((option) => (
            <ToggleGroupItem key={option.value} value={option.value}>
              {option.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </Field>
      <Field>
        <FieldLabel>Status</FieldLabel>
        <FieldDescription>
          Leave all unchecked to show every status.
        </FieldDescription>
        <CheckboxList
          idPrefix="task-filter-status"
          label="Status"
          options={ACTION_STATUS_OPTIONS}
          selected={value.statuses}
          onChange={(statuses) => set({ statuses })}
        />
      </Field>
      <Field>
        <FieldLabel>Priority</FieldLabel>
        <CheckboxList
          idPrefix="task-filter-priority"
          label="Priority"
          options={ACTION_PRIORITY_OPTIONS}
          selected={value.priorities}
          onChange={(priorities) => set({ priorities })}
        />
      </Field>
      {directory.users.length > 0 ? (
        <Field>
          <FieldLabel>Assignee</FieldLabel>
          <div className="tw:max-h-32 tw:overflow-y-auto">
            <CheckboxList
              idPrefix="task-filter-assignee"
              label="Assignee"
              options={directory.users}
              selected={value.assignees}
              onChange={(assignees) => set({ assignees })}
            />
          </div>
        </Field>
      ) : null}
      <Field>
        <div className="tw:flex tw:items-start tw:gap-2">
          <Switch
            id="task-filter-include-linked"
            checked={value.includeLinked}
            className="tw:mt-0"
            onCheckedChange={(checked) =>
              set({ includeLinked: checked === true })
            }
          />
          <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
            <Label
              htmlFor="task-filter-include-linked"
              className="tw:leading-5"
            >
              Always show generated and pinned tasks
            </Label>
            <FieldDescription>
              Tasks this section generated or someone added here stay visible
              even when they no longer match the filters above.
            </FieldDescription>
          </div>
        </div>
      </Field>
    </>
  );
}
